import { List, Text, View, fromTheme, styled, webOnlyStyles } from 'lib_ui-primitives';
import { GRID_DIMENSIONS } from 'lib_ui-primitives/src/styling/themes/gl2025/tables';
import { createElement as rc, useCallback, useEffect, useRef, useState } from 'react';
import { useTheme } from 'styled-components';
import {
    renderSingleGrid,
    renderPinnedGrid as sharedRenderPinnedGrid,
    renderScrollableGrid as sharedRenderScrollableGrid,
    useGridLogic,
    useSearchingText
} from './Grid.gl2025.shared';

// Container for grid + 'Searching...' text
const GridOuterContainer = styled(View).attrs({ name: 'GridOuterContainer' })`
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    padding-bottom: ${fromTheme('gridOuterContainer', 'paddingBottom')}
`;

// Container for both grids side-by-side
let GridBodyContainer = styled(View).attrs({ name: 'GridBodyContainer' })`
    display: flex;
    flex-direction: row;
    min-height: ${GRID_DIMENSIONS.minHeight}px;
    flex-grow: 1;
    position: relative;
    overflow: hidden; /* Prevent container-level scrolling */
`;
GridBodyContainer = webOnlyStyles(GridBodyContainer)`
    box-shadow: ${({ theme }) => theme.table?.shadow || 'none'};
`;

// Pinned columns container (fixed width, no horizontal scroll)
let PinnedContainer = styled(View).attrs({ name: 'PinnedContainer' })`
    flex-shrink: 0;
    position: sticky;
    left: 0;
    z-index: 2;
    border-right-width: ${GRID_DIMENSIONS.borderWidth}px;
    border-right-color: ${({ theme }) => theme.table.rowBorderColor};
    overflow: hidden; /* Hide container-level scrollbars */

    /* Hide scrollbars on all descendants but allow scrolling for sync */
    & * {
        scrollbar-width: none; /* Firefox */
        -ms-overflow-style: none; /* IE/Edge */

        &::-webkit-scrollbar {
            display: none; /* Chrome/Safari */
        }
    }
`;
PinnedContainer = webOnlyStyles(PinnedContainer)`
    box-shadow: ${({ theme }) => theme.table.pinnedDropShadow };
`;

// Scrollable columns container (flexible width, horizontal scroll)
const ScrollableContainer = styled(View).attrs({ name: 'ScrollableContainer' })`
    flex: 1;
    overflow-x: auto; /* Only show scrollbar when content overflows */
    overflow-y: hidden; /* Vertical scroll handled by List internally */
    min-width: 0; /* Allow flex item to shrink below content size */
`;

// Text shown when searching for records
const SearchingTextContainer = styled(Text).attrs({ name: 'SearchingText' })`
    position: absolute;
    bottom: ${fromTheme('table', 'searchingTextContainer', 'bottom')};
    left: ${fromTheme('table', 'searchingTextContainer', 'left')};
    font-family: ${fromTheme('font')};
    font-size: ${fromTheme('fontSize')};
`;

function GridGL2025(props) {
    const {
        id,
        records,
        recordCount,
        columnHNodes,
        columnWidths,
        HeaderRow,
        onClick,
        onRenderItem,
        onScrollLayoutChange, // Not used - two-grid has its own scroll sync handlers
        endOfRecordsText,
        awaitingFirstServerPage,
        localOnlySearchReason,
        offlineText,
        listType,
        hNode,
        headerClicked,
        sortedColumns,
        handleColumnWidthChange
    } = props;

    const theme = useTheme();
    const highlightedIndexRef = useRef(-1);

    // Track horizontal scrollbar visibility to sync pinned grid height
    const [hasHorizontalScrollbar, setHasHorizontalScrollbar] = useState(false);

    const searchingText = useSearchingText(recordCount, awaitingFirstServerPage, localOnlySearchReason);

    // Use shared business logic
    const {
        pinnedListRef,
        scrollableListRef,
        scrollableContainerRef,
        pinnedColumns,
        scrollableColumns,
        pinnedCount,
        pinnedWidth,
        handlePinnedScroll,
        handleScrollableScroll,
        PinnedRowDetail,
        ScrollableRowDetail,
        SingleGridRowDetail
    } = useGridLogic(props, theme);

    // Helper to apply/remove hover styles from matching rows in both grids
    const updateRowHover = useCallback((rowIndex, isHovering) => {
        // Skip hover for "End of Records" row (last row when endOfRecordsText is present)
        if (endOfRecordsText && rowIndex === recordCount) {
            return;
        }

        highlightedIndexRef.current = isHovering ? rowIndex : -1;

        const hoverBgColor = theme.table.rowHoverBackground;
        const hoverBorderColor = theme.table.rowHoverBorderColor;
        const allRows = document.querySelectorAll(`[data-grid-id="${id}"] [data-row-index="${rowIndex}"]`);

        allRows.forEach(element => {
            if (!(element instanceof HTMLElement)) return;

            // The element with data-row-index is the row wrapper (View)
            // Look for the first child which should be the Gl2025Row div
            const innerDiv = element.firstElementChild;
            if (!(innerDiv instanceof HTMLElement)) return;

            if (isHovering) {
                // Store original values before applying hover (only if not already stored)
                if (!innerDiv.hasAttribute('data-original-bg')) {
                    const computedStyle = window.getComputedStyle(innerDiv);
                    innerDiv.setAttribute('data-original-bg', computedStyle.backgroundColor);
                    innerDiv.setAttribute('data-original-border', computedStyle.borderBottom);
                }
                // Apply hover styles matching Gl2025Row hover with theme-based colors
                innerDiv.style.setProperty('background-color', hoverBgColor);
                innerDiv.style.setProperty('border-bottom', `${GRID_DIMENSIONS.selectedBorderWidth}px solid ${hoverBorderColor}`);
            } else {
                // Restore to original values stored in data attributes
                const originalBg = innerDiv.getAttribute('data-original-bg');
                const originalBorder = innerDiv.getAttribute('data-original-border');
                if (originalBg) {
                    innerDiv.style.setProperty('background-color', originalBg);
                }
                if (originalBorder) {
                    innerDiv.style.setProperty('border-bottom', originalBorder);
                }
            }
        });
    }, [theme, id, endOfRecordsText, recordCount]);

    // Detect horizontal scrollbar in scrollable grid to sync pinned grid padding
    useEffect(() => {
        const checkHorizontalScrollbar = () => {
            const scrollableList = scrollableListRef.current;

            if (scrollableList) {
                let outerElement = null;

                // Try _outerRef (internal react-window property)
                if (scrollableList._outerRef) {
                    outerElement = scrollableList._outerRef;
                }

                // Fallback: look for div with overflow in container
                if (!outerElement && scrollableContainerRef.current) {
                    const divs = scrollableContainerRef.current.querySelectorAll('div');
                    for (const div of divs) {
                        const style = window.getComputedStyle(div);
                        if (style.overflowX === 'auto' || style.overflowX === 'scroll') {
                            outerElement = div;
                            break;
                        }
                    }
                }

                if (outerElement) {
                    const hasScrollbar = outerElement.scrollWidth > outerElement.clientWidth;
                    setHasHorizontalScrollbar(hasScrollbar);
                }
            }
        };

        // Initial check with delay to let DOM settle
        const timeoutId = setTimeout(checkHorizontalScrollbar, 200);

        // Check on window resize and column changes
        window.addEventListener('resize', checkHorizontalScrollbar);

        return () => {
            clearTimeout(timeoutId);
            window.removeEventListener('resize', checkHorizontalScrollbar);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [recordCount, columnWidths, scrollableColumns.length, id]);

    // Create a Row wrapper that handles synchronized hover across both grids using direct DOM manipulation
    function SynchronizedRow(props) {
        const { children, 'data-row-index': rowIndex, style, ...restProps } = props;

        const enhancedProps = {
            ...restProps,
            style: { ...style, minWidth: '100%' }, // Ensure row fills container
            onMouseEnter: () => updateRowHover(rowIndex, true),
            onMouseLeave: () => updateRowHover(rowIndex, false),
            'data-row-index': rowIndex
        };
        return rc(View, enhancedProps, children);
    }

    // Platform components to pass to shared render functions
    const platformComponents = {
        Row: SynchronizedRow,
        PinnedContainer,
        ScrollableContainer,
        List,
        HeaderRow
    };

    // Render pinned grid using shared function
    const renderPinnedGrid = () => sharedRenderPinnedGrid(
        {
            id,
            records,
            recordCount,
            onClick,
            onRenderItem,
            listType,
            endOfRecordsText,
            offlineText,
            hNode,
            headerClicked,
            sortedColumns,
            columnWidths,
            handleColumnWidthChange,
            pinnedListRef,
            pinnedColumns,
            pinnedCount,
            pinnedWidth,
            handlePinnedScroll,
            PinnedRowDetail,
            hasHorizontalScrollbar
        },
        platformComponents
    );

    // Render scrollable grid using shared function
    const renderScrollableGrid = () => sharedRenderScrollableGrid(
        {
            id,
            records,
            recordCount,
            onClick,
            onRenderItem,
            listType,
            endOfRecordsText,
            offlineText,
            hNode,
            headerClicked,
            sortedColumns,
            columnWidths,
            handleColumnWidthChange,
            scrollableListRef,
            scrollableContainerRef,
            scrollableColumns,
            pinnedCount,
            handleScrollableScroll,
            ScrollableRowDetail
        },
        platformComponents
    );

    // If no pinned columns, render only scrollable grid with both corners rounded
    if (pinnedCount === 0) {
        return rc(GridOuterContainer, {},
            rc(GridBodyContainer, {},
                renderSingleGrid(
                    {
                        id,
                        records,
                        recordCount,
                        onScrollLayoutChange,
                        onClick,
                        onRenderItem,
                        listType,
                        endOfRecordsText,
                        offlineText,
                        hNode,
                        headerClicked,
                        sortedColumns,
                        columnHNodes,
                        columnWidths,
                        handleColumnWidthChange,
                        scrollableContainerRef,
                        scrollableListRef,
                        SingleGridRowDetail
                    },
                    {
                        Row: SynchronizedRow,
                        ScrollableContainer,
                        List,
                        HeaderRow
                    }
                ),
                rc(SearchingTextContainer, {}, searchingText)
            )
        );
    }

    // Otherwise render two-grid layout with split border radius
    return rc(GridOuterContainer, {},
        rc(GridBodyContainer, { 'data-grid-id': id },
            renderPinnedGrid(),
            renderScrollableGrid(),
            rc(SearchingTextContainer, {}, searchingText)
        )
    );
}

GridGL2025.displayName = 'GridGL2025';
export default GridGL2025;
